// BUILD-046 task 8.18 (supporting model): the ACTION error of the My Work host, kept apart from the LOAD error of a list
// (design "List-state model": "The legacy page used one `error` string plus `items = []`, which produced the error and empty states
// together. The new model splits `loadError` (list) from `actionError` (a failed Done or save).").
//
//   loadError   : lives in each list's state (listStateModel: phase `error`, with that list's own Retry). It is never written here.
//   actionError : a failed create, save, complete or restore. It is a dismissible banner; it never changes a list's phase, so a failed
//                 action can never make a loaded list look failed or empty, and a failed list load never fills this banner.
//
// An answer the client dropped because the Caller changed is neither data nor a failure (`isStaleOutcome`): it produces no message.
//
// Pure module: no React, no I/O, no runtime imports beyond the list-state helper. Erasable TypeScript only.

import { isStaleOutcome } from './listStateModel';

export interface ActionErrorState {
  /** The message to show, or null when there is nothing to show. */
  message: string | null;
}

export function initialActionError(): ActionErrorState {
  return { message: null };
}

/** The server's own message when there is one, else a reason derived from how the request failed. */
function reasonOf(thrown: unknown): string | null {
  if (thrown !== null && typeof thrown === 'object') {
    const t = thrown as { kind?: unknown; message?: unknown };
    if (t.kind === 'timeout') return 'There was no answer in time.';
    if (t.kind === 'network') return 'The server could not be reached.';
    if (typeof t.message === 'string' && t.message.trim() !== '') return t.message.trim();
  }
  if (typeof thrown === 'string' && thrown.trim() !== '') return thrown.trim();
  return null;
}

/** `The task was not saved. <reason>`; null for an answer dropped because the Caller changed. */
export function actionFailedMessage(thrown: unknown, headline: string): string | null {
  if (isStaleOutcome(thrown)) return null;
  const reason = reasonOf(thrown);
  return reason === null ? headline : `${headline} ${reason}`;
}

export function actionFailed(state: ActionErrorState, thrown: unknown, headline: string): ActionErrorState {
  const message = actionFailedMessage(thrown, headline);
  return message === null ? state : { message };
}

/** A new action starts, the banner is dismissed, or the Caller changed: nothing is shown any more. */
export function clearActionError(state: ActionErrorState): ActionErrorState {
  return state.message === null ? state : { message: null };
}
